import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { useSelector } from 'react-redux';
import { Prompt, useHistory } from 'react-router-dom';
import { makeStyles } from '@material-ui/core';
import Paper from '@material-ui/core/Paper';
import FormProvider from './FormProvider';
import Toolbar from './Toolbar';
import Header from './Header';
import Tabs from './Tabs';
import tabPropType from './Tabs/propTypes';
import PromptOnUnload from '../common/PromptOnUnload';
import ProgressFullscreen from '../common/ProgressFullscreen';
import ImmutablePropsToJS from '../../utils/ImmutablePropsToJS';
import { getIsDataDirty, getLanguage } from '../../features/app/selectors';
import { dataLabelsPropTypes } from './propTypes';
import { States } from 'features/PpsiContainer/constants';

export const TOOLBAR_HEIGHT = 64;

const useStyles = makeStyles(theme => ({
  main: {
    overflow: 'hidden',
    flexGrow: 1,
    maxWidth: theme.breakpoints.values.lg,
    margin: '0 auto',
    padding: theme.spacing(4)
  },
  content: {
    position: 'relative',
    overflowY: 'scroll',
    maxHeight: `calc(100vh - ${TOOLBAR_HEIGHT}px * 2)`
  },
  infoContainer: {
    textAlign: 'center',
    padding: theme.spacing(4)
  }
}));

function getDefaultData(language, defaultData = {}) {
  return _.isFunction(defaultData) ? defaultData({ language }) : defaultData;
}

/**
 * Gets data for the form. The data and defaultData is merged. The default data
 * is used in case no data exists.
 *
 * @param language Selected language
 * @param data Data in the form
 * @param defaultDataProp Default data in the form, either an object and function
 */
function getFormData(language, data, defaultDataProp) {
  const defaultData = getDefaultData(language, defaultDataProp);
  return _.merge({}, defaultData, data);
}

const getPromptMessage = history => location => {
  // Only changes in location's hash
  if (
    location.pathname === history.location.pathname &&
    location.search === history.location.search
  ) {
    return true;
  }
  return 'app.confirmPageLeaveMessage';
};

const Form = ({
  data = {},
  defaultData = {},
  tabs,
  useMarketLanguage,
  dataLabels = {},
  ToolbarContent,
  showProgress = false,
  completePpsiData
}) => {
  const classes = useStyles();
  const language = useSelector(getLanguage);
  const isDataDirty = useSelector(getIsDataDirty);
  const history = useHistory();
  const formData = getFormData(language, data, defaultData);
  return (
    <FormProvider
      data={formData}
      dataLabels={dataLabels}
      completePpsiData={completePpsiData}
    >
      <Prompt when={isDataDirty} message={getPromptMessage(history)} />
      <PromptOnUnload />
      <ProgressFullscreen open={showProgress} />
      <Toolbar ContentRight={ToolbarContent} />
      <div id="scrollable" className={classes.content}>
        <div className={classes.main}>
          <Paper>
            <Header useMarketLanguage={useMarketLanguage} />
            <Tabs tabs={tabs} />
          </Paper>
        </div>
      </div>
    </FormProvider>
  );
};

Form.propTypes = {
  // Data in the form
  data: PropTypes.object,
  // Default data, used in case no data exists
  defaultData: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  tabs: PropTypes.arrayOf(PropTypes.shape(tabPropType)).isRequired,
  dataLabels: PropTypes.shape(dataLabelsPropTypes),
  ToolbarContent: PropTypes.func,
  showProgress: PropTypes.bool
};

export default ImmutablePropsToJS(Form);
